import { useEffect, useState, type JSX } from 'react';
import { Check, Loader2, MessageSquare, Search, Send, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { EmptyState } from '@/components/ui/empty-state';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/ui/page-header';
import { Combobox } from '@/components/ui/combobox';
import { PlatformIcon } from '@/components/PlatformIcon';
import { InviteStagePill } from '@/components/invites/InviteStagePill';
import { useProspectSearch } from '@/hooks/useProspectSearch';
import { useOutreachHistory } from '@/hooks/useOutreachHistory';
import { useRecordContact, useWithdrawContact } from '@/hooks/useContacts';
import { useDebounce } from '@/hooks/useDebounce';
import { useMe } from '@/hooks/useMe';
import { INVITE_CHANNELS, type ContactAttempt, type InviteChannel, type Me } from '@/api/types';
import {
  INVALID_HANDLE,
  STEPS,
  TWO_STEP_SHORT,
  WHY_MARK_MESSAGED,
  channelLabel,
  leadStatusLabel,
  normalizeHandle,
} from '@/lib/outreach-copy';
import { creatorTypeLabel, teamLabel } from '@/lib/labels';
import { formatDate } from '@/lib/dates';

/**
 * Creator lookup.
 *
 * Two layers, because "has anyone worked this handle" has two different
 * answers. The *logged reply* answer (someone logged the creator's reply and
 * owns the invite) is what the substring search returns. The *messaged*
 * answer (possibly several people, none of whom have a reply yet) only exists
 * once a channel is picked, since messaged records are keyed on
 * (channel, handle).
 *
 * So: search by substring to find the handle, then the messaged timeline and
 * the "I messaged them" action appear once the channel is known. Without a
 * channel we can't tell TikTok's @foo from Instagram's @foo, and showing a
 * merged history would be wrong.
 */
export function ProspectsPage(): JSX.Element {
  const [q, setQ] = useState('');
  const [channel, setChannel] = useState<'' | InviteChannel>('');
  // Debounce so we don't fire a request on every keystroke. 250ms is short
  // enough to feel live and long enough to skip the burst typing produces.
  const debouncedQ = useDebounce(q, 250);

  const me = useMe();
  const outreacher: Extract<Me, { role: 'outreacher' }> | null =
    me.data && me.data.role === 'outreacher' ? me.data : null;
  const myInviterId = outreacher?.inviter.id ?? null;
  const channels = lookupChannels(outreacher?.team?.allowedChannels);

  // A team limited to one channel has nothing to pick, so pick it for them.
  // Only once: if they clear it back to "Any channel" that choice sticks.
  const onlyChannel = channels.length === 1 ? channels[0] : undefined;
  const [defaulted, setDefaulted] = useState(false);
  useEffect(() => {
    if (onlyChannel && !defaulted) {
      setChannel(onlyChannel);
      setDefaulted(true);
    }
  }, [onlyChannel, defaulted]);

  const typed = debouncedQ.trim();
  // What the backend stores for this handle on this channel. Normalising here
  // means the history we query and the record we write are the same creator
  // as the one shown ("Saved as @handle"), even when a profile link was pasted.
  const handle = channel ? normalizeHandle(channel, typed) : null;
  const invalidHandle = channel !== '' && typed.length > 0 && handle === null;

  const search = useProspectSearch({
    q: handle?.username ?? typed.replace(/^@/, ''),
    ...(channel ? { channel } : {}),
  });
  const history = useOutreachHistory({
    channel,
    username: handle?.username ?? '',
  });
  const attempts = history.data?.attempts ?? [];
  const myAttempt =
    myInviterId === null ? undefined : attempts.find((a) => a.inviterId === myInviterId);

  const recordContact = useRecordContact();
  const withdrawContact = useWithdrawContact();
  const contactPending = recordContact.isPending || withdrawContact.isPending;

  function markMessaged(): void {
    if (!channel || !handle) return;
    recordContact.mutate(
      { channel, targetIdentifier: handle.username },
      {
        onSuccess: () =>
          toast.success(`Marked ${handle.displayHandle} as messaged`, {
            description: `Saved as ${handle.displayHandle} on ${channelLabel(channel)}.`,
          }),
        onError: (e: unknown) => toast.error(contactErrorMessage(e, 'record')),
      },
    );
  }

  function undoMessaged(): void {
    if (!channel || !handle) return;
    withdrawContact.mutate(
      { channel, targetIdentifier: handle.username },
      {
        onSuccess: () => toast.success('Removed your messaged mark'),
        onError: (e: unknown) => toast.error(contactErrorMessage(e, 'withdraw')),
      },
    );
  }

  const hasInput = q.trim().length > 0;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="Creator lookup"
        description="See who has messaged a creator, when, and who logged their reply. Search by handle (with or without @), part of one, or a profile link."
      />

      <div className="grid gap-3 sm:grid-cols-[1fr_180px]">
        <div className="grid gap-1.5">
          <Label htmlFor="q">Handle</Label>
          <Input
            id="q"
            autoFocus
            value={q}
            placeholder="e.g. khabylame or part of a handle"
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="channel">Channel</Label>
          <Combobox
            value={channel}
            onChange={(v) => setChannel(v as '' | InviteChannel)}
            options={channels.map((c) => ({ value: c, label: channelLabel(c) }))}
            allowAll
            allLabel="Any channel"
            placeholder="Any channel"
          />
        </div>
      </div>

      {!hasInput ? (
        <EmptyState
          icon={<Search className="h-6 w-6" />}
          title="Type a handle to search."
          hint="Shows who has messaged them and who logged their reply."
        />
      ) : (
        <>
          {/* ── Messaged timeline ── */}
          {channel === '' && (
            <p className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
              Pick a channel to see who has messaged this creator. The same handle on two
              platforms is two different creators.
            </p>
          )}

          {invalidHandle && (
            <p className="rounded-md bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
              {INVALID_HANDLE}
            </p>
          )}

          {channel !== '' && handle && (
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Who has messaged {handle.displayHandle}
                </h2>
              </div>

              {history.isLoading ? (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading history
                </div>
              ) : history.isError ? (
                <p className="rounded-md bg-muted/30 px-4 py-3 text-sm text-destructive">
                  {(history.error as Error).message}
                </p>
              ) : (
                <>
                  {attempts.length === 0 ? (
                    <p className="rounded-md bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
                      {noMessagedRecordText(handle.displayHandle, channel)}
                    </p>
                  ) : (
                    <ol className="space-y-2">
                      {attempts.map((a, i) => (
                        <AttemptRow
                          key={a.publicId}
                          attempt={a}
                          order={i + 1}
                          isMe={myInviterId !== null && a.inviterId === myInviterId}
                        />
                      ))}
                    </ol>
                  )}

                  {history.data?.prospect && (
                    <p className="text-xs text-muted-foreground">
                      Lead queue status: {leadStatusLabel(history.data.prospect.status)}.
                    </p>
                  )}

                  {outreacher && (
                    <div className="flex flex-wrap items-center gap-3 rounded-md border border-border/60 px-4 py-3">
                      {myAttempt?.resultingInvitePublicId ? (
                        <p className="flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400">
                          <Trophy className="h-3.5 w-3.5" />
                          You logged their reply. Find it in My creators.
                        </p>
                      ) : myAttempt ? (
                        <>
                          <span
                            className="flex items-center gap-1 text-sm font-medium text-sky-600 dark:text-sky-400"
                            title={WHY_MARK_MESSAGED}
                          >
                            <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                            {STEPS.messaged.done}
                          </span>
                          <span className="text-muted-foreground">·</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            disabled={contactPending}
                            onClick={undoMessaged}
                            title="Remove your messaged mark. Teammates' marks stay."
                          >
                            {withdrawContact.isPending ? 'Undoing…' : STEPS.messaged.undo}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={contactPending}
                            onClick={markMessaged}
                            title={WHY_MARK_MESSAGED}
                          >
                            <Send className="mr-1 h-3.5 w-3.5" />
                            {recordContact.isPending ? 'Saving…' : STEPS.messaged.verb}
                          </Button>
                          <span className="text-xs text-muted-foreground">
                            Saves {handle.displayHandle} on {channelLabel(channel)}. Not a
                            log, and nothing is owed for it.
                          </span>
                        </>
                      )}
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground">{TWO_STEP_SHORT}</p>
                </>
              )}
            </section>
          )}

          {/* ── Logged replies (substring search across channels) ── */}
          <section className="space-y-3">
            <h2 className="text-xs uppercase tracking-wide text-muted-foreground">
              {search.isLoading
                ? 'Searching logged replies'
                : `${search.data?.length ?? 0} logged ${
                    search.data?.length === 1 ? 'reply' : 'replies'
                  }`}
            </h2>

            {search.isLoading ? (
              <div className="flex items-center text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Searching
              </div>
            ) : search.isError ? (
              <p className="rounded-md bg-muted/30 px-4 py-3 text-sm text-destructive">
                {(search.error as Error).message}
              </p>
            ) : !search.data || search.data.length === 0 ? (
              <p className="rounded-md bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
                Nobody has logged a reply from a matching creator yet.
              </p>
            ) : (
              <ul className="space-y-3">
                {search.data.map((m) => (
                  <li
                    key={m.id}
                    className="grid gap-2 rounded-md bg-muted/30 px-4 py-3 sm:grid-cols-[1fr_auto]"
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                        <PlatformIcon
                          platform={m.channel}
                          size={14}
                          className="text-muted-foreground"
                        />
                        <span>@{m.targetIdentifier}</span>
                        <span className="text-xs font-normal text-muted-foreground">
                          {channelLabel(m.channel)}
                        </span>
                        <InviteStagePill invite={{ status: m.status }} />
                      </div>
                      <span className="text-xs text-muted-foreground">
                        Reply logged by{' '}
                        <span className="font-medium text-foreground">
                          {myInviterId !== null && m.inviterId === myInviterId ? 'you' : m.inviterName}
                        </span>{' '}
                        ({teamLabel(m.inviterTeam)}) on {formatDate(m.createdAt)}
                        {m.convertedAt ? ` · Posted ${formatDate(m.convertedAt)}` : ''}
                      </span>
                    </div>
                    <span className="self-center text-[10px] uppercase tracking-wider text-muted-foreground">
                      {creatorTypeLabel(m.category)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/**
 * One outreacher's messaged record. The one whose record produced an invite
 * gets the trophy and a highlighted border: that's the answer to "who logged
 * the reply", which is otherwise invisible in a list ordered by time.
 */
function AttemptRow({
  attempt,
  order,
  isMe,
}: {
  attempt: ContactAttempt;
  order: number;
  isMe: boolean;
}): JSX.Element {
  const won = attempt.resultingInvitePublicId !== null;
  return (
    <li
      className={`flex items-start gap-3 rounded-md border px-4 py-3 ${
        won
          ? 'border-emerald-500/40 bg-emerald-500/[0.06]'
          : 'border-border/60 bg-muted/30'
      }`}
    >
      <span
        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
          won ? 'bg-emerald-500/20 text-emerald-600' : 'bg-muted text-muted-foreground'
        }`}
      >
        {won ? <Trophy className="h-3.5 w-3.5" /> : order}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">{isMe ? 'You' : attempt.inviterName}</span>
          <span className="text-xs text-muted-foreground">({teamLabel(attempt.inviterTeam)})</span>
          {won && (
            <span className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
              Logged the reply
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {messagedWhenText(attempt)}
        </p>
        {attempt.note && (
          <p className="mt-1 text-xs text-foreground/80">{attempt.note}</p>
        )}
      </div>
    </li>
  );
}

// ── Pure helpers (tested in src/lib/__tests__/queue-copy.test.ts) ─────────

/** The channels this outreacher can look up: their team's, else all of them. */
export function lookupChannels(
  allowed: ReadonlyArray<InviteChannel> | null | undefined,
): InviteChannel[] {
  return allowed && allowed.length > 0 ? [...allowed] : [...INVITE_CHANNELS];
}

/**
 * Empty messaged history. Deliberately not "you're clear to message them":
 * we only know about messages someone recorded.
 */
export function noMessagedRecordText(displayHandle: string, channel: string): string {
  return `No one has recorded messaging ${displayHandle} on ${channelLabel(channel)}. We can't see messages nobody recorded.`;
}

/** "Messaged 29 Sep 2026" or "Messaged 3 times, first 1 Sep 2026, last 29 Sep 2026". */
export function messagedWhenText(
  attempt: Pick<ContactAttempt, 'firstContactedAt' | 'lastContactedAt' | 'attemptCount'>,
): string {
  if (attempt.attemptCount > 1) {
    return `Messaged ${attempt.attemptCount} times, first ${formatDate(attempt.firstContactedAt)}, last ${formatDate(attempt.lastContactedAt)}`;
  }
  return `Messaged ${formatDate(attempt.firstContactedAt)}`;
}

/**
 * Error copy for "I messaged them" / "Undo" on the lookup. The withdraw
 * endpoint answers 404 once a reply is logged (or when there is no mark of
 * yours to remove), which the raw message doesn't explain.
 */
export function contactErrorMessage(err: unknown, action: 'record' | 'withdraw'): string {
  const status =
    typeof err === 'object' && err !== null && 'status' in err
      ? (err as { status: unknown }).status
      : null;
  if (action === 'withdraw' && status === 404) {
    return "Can't undo. Their reply is already logged, or there's no messaged mark of yours to remove.";
  }
  if (err instanceof Error && err.message) return err.message;
  return action === 'record'
    ? "Couldn't save that you messaged them. Try again."
    : "Couldn't remove your messaged mark. Try again.";
}
